import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TabsController } from "../context/tabsContext";

const useTabs = (count: number, urlParameterName?: string): TabsController => {
  const tab = urlParameterName ?? "tab";
  const [searchParams, setSearchParams] = useSearchParams();

  // Set the initial selectedIndex from the URL, or default to 0 if it's invalid
  const [selectedIndex, setSelectedIndex] = useState(() => {
    const val = Number(searchParams.get(tab));
    return isNaN(val) ? 0 : val;
  });

  const goToTab = (index: number) => {
    setSelectedIndex(index);

    // Functional form so this always merges into the latest params instead of overwriting
    // with a snapshot captured at render time — other mounted tabs' own useListFilters
    // instances can be writing their own params concurrently.
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set(tab, `${index}`);
        return nextParams;
      },
      { replace: false },
    );
  };

  const previous = () => selectedIndex > 0 && goToTab(selectedIndex - 1);
  const next = () => selectedIndex < count - 1 && goToTab(selectedIndex + 1);

  useEffect(() => {
    const t = Number(searchParams.get(tab));
    if (!isNaN(t) && t !== selectedIndex) {
      setSelectedIndex(t);
    }
  }, [searchParams, tab]);

  return { selectedIndex, goToTab, previous, next, urlParameter: tab };
};

export default useTabs;
